/* ========================================================================== */
/* src/api/index.js - Register all api endpoints for react app */
/* ========================================================================== */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

//A robust API fetch utility that handles authentication and automatic token refreshing.
const fetchApi = async (url, options = {}) => {
    let token = localStorage.getItem('authToken');
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(url, { ...options, headers });

        if (!response.ok) {
            const error = new Error('An API error occurred');
            error.response = response;
            throw error;
        }

        if (response.status === 204) { return; }
        return response.json();

    } catch (error) {
        if (!error.response) {
            console.error("Network error or server is down:", error);
            throw new Error('Cannot connect to the server. Please check your network connection.');
        }
        const originalRequest = { url, options };
        const status = error.response ? error.response.status : null;

        if (status !== 401) {
            const errorData = await error.response.json().catch(() => ({error: "Unknown API error"}));
            throw new Error(errorData.error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
            .then(() => fetchApi(url, options))
            .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        return new Promise((resolve, reject) => {
            api.refreshToken()
                .then(({ accessToken }) => {
                    localStorage.setItem('authToken', accessToken);
                    processQueue(null, accessToken);
                    resolve(fetchApi(url, options));
                })
                .catch(err => {
                    processQueue(err, null);
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    window.location.href = '/login';
                    reject(err);
                })
                .finally(() => {
                    isRefreshing = false;
                });
        });
    }
};


export const api = {
  /* Authentication Endpoints
  */
  login: async (credentials) => {
    if (!API_BASE_URL) {
        throw new Error("The API server address is not configured. Please check your environment settings.");
    }
    let response;
    try {
        response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        });
      } catch (e) {
          throw new Error('Login failed due to a server error. Please reach out to Support.');
      }
    if (!response.ok) { 
      try {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed due to a server error.');
      } catch (e) {
        throw new Error('The server is currently unavailable. Please try again later.');
      }
    }
    return response.json();
  },
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken || refreshToken === 'undefined') {
        return Promise.reject(new Error("No valid refresh token in storage."));
    }
    return fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
    }).then(res => {
        if (!res.ok) {
            throw new Error('Server rejected the refresh token.');
        }
        return res.json();
    });
  },
  register: (userData) => fetchApi(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      body: JSON.stringify(userData),
  }),
  forgotPassword: (email) => fetchApi(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  resetPassword: (token, password) => fetchApi(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  }),

  /* Property Management Endpoints
  */
  getProperties: () => fetchApi(`${API_BASE_URL}/properties`),
  createProperty: (propertyData) => fetchApi(`${API_BASE_URL}/properties`, {
    method: 'POST',
    body: JSON.stringify(propertyData),
  }),
  updateProperty: (id, propertyData) => fetchApi(`${API_BASE_URL}/properties/${id}`, {
    method: 'PUT',
    body: JSON.stringify(propertyData),
  }),
  updatePropertySettings: (id, settings) => fetchApi(`${API_BASE_URL}/properties/${id}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  }),
  /* User Management Endpoints
  */
  getUsers: () => fetchApi(`${API_BASE_URL}/users`),
  getUserByEmail: (email) => fetchApi(`${API_BASE_URL}/users/lookup/${email}`),
  createAdminUser: (userData) => fetchApi(`${API_BASE_URL}/users`, {
    method: 'POST',
    body: JSON.stringify(userData),
  }),

  /* Device Management Endpoints
  */
  getDevices: (filters) => {
      const params = new URLSearchParams(filters);
      return fetchApi(`${API_BASE_URL}/devices?${params.toString()}`);
  },
  createDevice: (deviceData) => fetchApi(`${API_BASE_URL}/devices`, {
    method: 'POST',
    body: JSON.stringify(deviceData),
  }),
  updateDevice: (id, deviceData) => fetchApi(`${API_BASE_URL}/devices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(deviceData),
  }),
  rebootDevice: (deviceId) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/reboot`, {
    method: 'POST',
  }),
  setDeviceWifi: (deviceId, wifiData) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/wifi`, {
    method: 'POST',
    body: JSON.stringify(wifiData),
  }),
  setDeviceTime: (deviceId, timeData) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/time`, {
    method: 'POST',
    body: JSON.stringify(timeData),
  }),
  setDeviceAccessTimeZone: (deviceId, scheduleData) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/access-time-zone`, {
    method: 'POST',
    body: JSON.stringify(scheduleData),
  }),
  getDeviceAccessTimeZone: (deviceId, timeZoneNumber) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/access-time-zone/${timeZoneNumber}`),
  getNtpSettings: (deviceId) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/ntp-settings`),
  setNtpSettings: (deviceId, settings) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/ntp-settings`, {
    method: 'POST',
    body: JSON.stringify(settings),
  }),

  /* Workforce & Time Management Endpoints
  */
  getWorkforce: (filters) => {
      const params = new URLSearchParams(filters);
      return fetchApi(`${API_BASE_URL}/workforce?${params.toString()}`);
  },
  updateWorkforcePerson: (personId, data) => fetchApi(`${API_BASE_URL}/workforce/${personId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getWorkSummary: (filters) => fetchApi(`${API_BASE_URL}/reports/work-summary`, {
      method: 'POST',
      body: JSON.stringify(filters)
  }),
  createTimeEntry: (entryData) => fetchApi(`${API_BASE_URL}/time-entries`, {
    method: 'POST',
    body: JSON.stringify(entryData),
  }),
  updateTimeEntry: (entryId, entryData) => fetchApi(`${API_BASE_URL}/time-entries/${entryId}`, {
    method: 'PUT',
    body: JSON.stringify(entryData),
  }),
  
  /* Reporting Endpoints
  */
  getPunchesReport: (filters) => fetchApi(`${API_BASE_URL}/reports/punches`, {
      method: 'POST',
      body: JSON.stringify(filters)
  }),
  sendTimeEntries: (reportData) => fetchApi(`${API_BASE_URL}/reports/send-time-entries`, {
      method: 'POST',
      body: JSON.stringify(reportData)
  }),
  getOpenPunches: (filters) => {
      const params = new URLSearchParams(filters);
      return fetchApi(`${API_BASE_URL}/reports/open-punches?${params.toString()}`);
  },

  /* Dashboard Endpoints
  */
  getDashboardReport: (filters) => {
    const params = new URLSearchParams(filters);
    return fetchApi(`${API_BASE_URL}/reports/dashboard-chart?${params.toString()}`);
  },

  /* Employee Enrollment Endpoints*/
getNextEmployeeId: (deviceId) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/next-employee-id?t=${Date.now()}`),

initiateFpEnrollment: (deviceId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/initiate`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

// MODIFIED: This function now accepts 'data' and sends a POST request.
checkFpEnrollmentStatus: (deviceId, userId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/status/${userId}`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

// This finalize function is no longer used by the new modal, but is left for reference.
finalizeFpEnrollment: (deviceId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/finalize`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

initiateFaceEnrollment: (deviceId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/initiate-face`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

// MODIFIED: This function for face enrollment also needs to be a POST.
checkFaceEnrollmentStatus: (deviceId, userId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/status/${userId}`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

finalizeFaceEnrollment: (deviceId, data) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/finalize-face`, {
    method: 'POST',
    body: JSON.stringify(data),
}),

checkAddFingerStatus: (deviceId, userId) => fetchApi(`${API_BASE_URL}/devices/${deviceId}/enrollment/status-add-finger/${userId}`),


  /* Case Management Endpoints 
  */
  getCases: () => fetchApi(`${API_BASE_URL}/cases`),
  getCaseById: (caseId) => fetchApi(`${API_BASE_URL}/cases/${caseId}`),
  createCase: (caseData) => fetchApi(`${API_BASE_URL}/cases`, {
      method: 'POST',
      body: JSON.stringify(caseData)
  }),
  addCaseComment: (caseId, commentData) => {
    console.log("Verifying FormData content before sending:");
    for (let [key, value] of commentData.entries()) {
      console.log(`- ${key}: ${value}`);
    }
    return fetch(`${API_BASE_URL}/cases/${caseId}/comments`, {
        method: 'POST',
        body: commentData,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
    }).then(response => {
        if (!response.ok) {
            throw new Error(`Network response was not ok, status: ${response.status}`);
        }
        return response.json();
    });
  },
  updateCase: (caseId, updateData) =>  fetchApi(`${API_BASE_URL}/cases/${caseId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  }),
  updateCaseStatus: (caseId, statusData) => fetchApi(`${API_BASE_URL}/cases/${caseId}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData)
  }),

   /*
    Billing & Invoicing Endpoints
  */
  getInvoices: () => fetchApi(`${API_BASE_URL}/invoices`),
  recordInvoicePayment: (invoiceId, paymentData) => fetchApi(`${API_BASE_URL}/invoices/${invoiceId}/record-payment`, {
    method: 'POST',
    body: JSON.stringify(paymentData),
  }),

  /*
    Product & Subscription Endpoints
  */
  getProducts: () => fetchApi(`${API_BASE_URL}/products`),
  getPropertySubscriptions: (propertyId) => fetchApi(`${API_BASE_URL}/properties/${propertyId}/subscriptions`),
  createSubscription: (subscriptionData) => fetchApi(`${API_BASE_URL}/subscriptions`, {
    method: 'POST',
    body: JSON.stringify(subscriptionData),
  }),

  /*
    AWS Images Endpoints
  */
  downloadAllImageFiles: (bucketName) => {
    return new Promise((resolve, reject) => {
        try {
            const url = `${API_BASE_URL}/download/${bucketName}`;
            window.location.href = url;
            setTimeout(() => {
                resolve({ message: 'Download initiated!' });
            }, 300);
        } catch (error) {
            reject(error);
        }
    });
  },
  downloadSpecificImageFile: (bucketName, fileName) => {
      const url = `${API_BASE_URL}/download/${bucketName}/${fileName}`;
      window.location.href = url;
  },

  /* Diagnostic Endpoints */
  getDiagnosticEvents: (filters) => {
      const params = new URLSearchParams(filters);
      return fetchApi(`${API_BASE_URL}/diagnostics/events?${params.toString()}`);
  },


  /* Push Notification Endpoints */
  savePushSubscription: (subscription) => fetchApi(`${API_BASE_URL}/push/save-subscription`, {
    method: 'POST',
    body: JSON.stringify(subscription),
  }),
  removePushSubscription: () => fetchApi(`${API_BASE_URL}/push/remove-subscription`, {
    method: 'DELETE',
  }),
  getUserPreferences: () => fetchApi(`${API_BASE_URL}/user/preferences`),
  updateUserPreferences: (preferences) => fetchApi(`${API_BASE_URL}/user/preferences`, {
    method: 'PATCH',
    body: JSON.stringify(preferences),
  }),

  /* Payroll Endpoints */
  getPaymentStatementDraft: async (filters) => {
    const params = new URLSearchParams(filters);
    const token = localStorage.getItem('authToken');
    
    // Use raw fetch to handle 404 gracefully
    const response = await fetch(`${API_BASE_URL}/payroll/statements/draft?${params.toString()}`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
        }
    });

    // If 404, simply return null (no draft exists)
    if (response.status === 404) return null;
    
    // Throw error for other issues (500, 403, etc)
    if (!response.ok) throw new Error('Failed to fetch draft');
    
    return response.json();
  },
  // Corrected getPaymentStatement
  getPaymentStatement: async (filters) => {
    const params = new URLSearchParams(filters);
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}/payroll/statements/lookup?${params.toString()}`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
        }
    });

    if (response.status === 404) return null; 
    if (!response.ok) throw new Error('Failed to fetch statement');
    return response.json();
  },
  submitPaymentStatement: (payload) => fetchApi(`${API_BASE_URL}/payroll/statements`, {
      method: 'POST',
      body: JSON.stringify(payload)
  }),

  /* LogIn As Endpoints */
  impersonateUser: (targetUserId) => fetchApi(`${API_BASE_URL}/auth/impersonate`, {
      method: 'POST',
      body: JSON.stringify({ targetUserId }),
  }),
};